import React from 'react';
import Icon, { IconName } from './Icon';

export interface KPICardProps {
  title: string;
  value: string | number;
  unit?: string;
  icon?: IconName;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
    period?: string;
    unit?: string;
    detail?: string;
    colorOverride?: string;
  };
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  style?: React.CSSProperties;
  tooltip?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  unit,
  icon,
  trend,
  variant = 'default',
  size = 'md',
  className,
  style,
  tooltip
}) => {
  // Size configurations
  const sizeConfig = {
    sm: {
      padding: '16px',
      titleSize: '12px',
      valueSize: '18px',
      iconSize: '16px',
      trendSize: '11px'
    },
    md: {
      padding: '20px',
      titleSize: '14px',
      valueSize: '24px',
      iconSize: '20px',
      trendSize: '12px'
    },
    lg: {
      padding: '24px',
      titleSize: '16px',
      valueSize: '32px',
      iconSize: '24px',
      trendSize: '14px'
    }
  };

  // Variant color configurations
  const variantConfig = {
    default: {
      background: '#ffffff',
      border: '#e5e7eb',
      titleColor: '#6b7280',
      valueColor: '#111827',
      iconColor: '#6b7280'
    },
    success: {
      background: '#f0fdf4',
      border: '#bbf7d0',
      titleColor: '#166534',
      valueColor: '#14532d',
      iconColor: '#16a34a'
    },
    warning: {
      background: '#fffbeb',
      border: '#fed7aa',
      titleColor: '#92400e',
      valueColor: '#78350f',
      iconColor: '#f59e0b'
    },
    danger: {
      background: '#fef2f2',
      border: '#fecaca',
      titleColor: '#991b1b',
      valueColor: '#7f1d1d',
      iconColor: '#ef4444'
    },
    info: {
      background: '#f0f9ff',
      border: '#bae6fd',
      titleColor: '#1e40af',
      valueColor: '#1e3a8a',
      iconColor: '#3b82f6'
    }
  };

  // Trend color configuration
  const getTrendColor = (direction: 'up' | 'down' | 'neutral') => {
    switch (direction) {
      case 'up': return '#16a34a';
      case 'down': return '#ef4444';
      case 'neutral': return '#6b7280';
      default: return '#6b7280';
    }
  };

  // Trend arrow
  const getTrendIcon = (direction: 'up' | 'down' | 'neutral') => {
    switch (direction) {
      case 'up': return '↗';
      case 'down': return '↘';
      case 'neutral': return '→';
      default: return '→';
    }
  };

  const currentSize = sizeConfig[size];
  const currentVariant = variantConfig[variant];

  const cardStyle: React.CSSProperties = {
    background: currentVariant.background,
    border: `1px solid ${currentVariant.border}`,
    borderRadius: '12px',
    padding: currentSize.padding,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.2s ease-in-out',
    cursor: 'default',
    fontFamily: 'Inter, system-ui, sans-serif',
    ...style
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: size === 'sm' ? '8px' : '12px'
  };

  const titleStyle: React.CSSProperties = {
    fontSize: currentSize.titleSize,
    fontWeight: '500',
    color: currentVariant.titleColor,
    margin: 0,
    lineHeight: '1.4'
  };

  const iconStyle: React.CSSProperties = {
    width: currentSize.iconSize,
    height: currentSize.iconSize,
    color: currentVariant.iconColor,
    flexShrink: 0
  };

  const valueContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
    marginBottom: trend ? (size === 'sm' ? '8px' : '12px') : '0'
  };

  const valueStyle: React.CSSProperties = {
    fontSize: currentSize.valueSize,
    fontWeight: '700',
    color: currentVariant.valueColor,
    lineHeight: '1.2',
    margin: 0
  };

  const unitStyle: React.CSSProperties = {
    fontSize: size === 'sm' ? '12px' : size === 'lg' ? '16px' : '14px',
    fontWeight: '500',
    color: currentVariant.titleColor,
    opacity: 0.8
  };

  const trendStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: currentSize.trendSize,
    fontWeight: '500',
    color: trend ? (trend.colorOverride ?? getTrendColor(trend.direction)) : '#6b7280'
  };

  return (
    <div
      className={className}
      style={cardStyle}
      title={tooltip}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)';
      }}
    >
      {/* Header with title and icon */}
      <div style={headerStyle}>
        <h3 style={titleStyle}>{title}</h3>
        {icon && (
          <Icon 
            name={icon} 
            className=""
            style={iconStyle}
          />
        )}
      </div>

      {/* Value and unit */}
      <div style={valueContainerStyle}>
        <span style={valueStyle}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </span>
        {unit && <span style={unitStyle}>{unit}</span>}
      </div>

      {/* Trend indicator */}
      {trend && (
        <div style={trendStyle}>
          <span>{getTrendIcon(trend.direction)}</span>
          <span>
            {trend.value > 0 ? '+' : trend.value < 0 ? '-' : ''}
            {Math.abs(trend.value)}
            {trend.unit ?? '%'}
          </span>
          {trend.period && (
            <span style={{ opacity: 0.7 }}>
              {trend.period}
            </span>
          )}
          {trend.detail && (
            <span style={{ opacity: 0.7 }}>
              {trend.detail}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default KPICard;